/**
 * CE QUE LA VENDEUSE A COMME IA — lecture seule, pour la pastille « Demander un changement ».
 *
 * Les clés se posent dans les Réglages du dashboard (BYOK, feature 0059 lot S1) ; le builder ne fait
 * que LIRE la liste pour proposer un choix au moment du prompt : « celle-là, c'est OpenAI qui l'écrit ».
 *
 * La liste vient du serveur, jamais d'une constante ici : une liste écrite en dur redeviendrait, le
 * jour où un fournisseur est retiré côté back, un menu qui ne commande rien (la règle du 24/08).
 *
 * Une seule clé par fournisseur, et elle sert TOUT ce que ce fournisseur sait faire — donc le menu du
 * texte et celui des images ne sont pas les mêmes : Anthropic écrit sans savoir dessiner.
 */
import { apiRequest } from './http';

const KEYS_PATH = '/api/store/ai/keys';
const PREFERRED_PATH = '/api/store/ai/keys/preferred';

/**
 * LES TRAVAUX QUE LE CHOIX DU BUILDER COMMANDE.
 *
 * Depuis le 27/08 le routage a quitté les Réglages (« remove that div ») : la pastille du builder est
 * le SEUL endroit où la vendeuse dit qui travaille pour elle. Son choix « rédaction » vaut donc pour
 * tout ce qui écrit — la page, le brief des visuels, la lecture des avis — et son choix « images »
 * pour ce qui dessine. Un seul geste, à l'endroit où elle voit le travail se faire.
 */
const TEXT_JOBS = ['COPY', 'IMAGE_BRIEF', 'RESEARCH'];
const IMAGE_JOBS = ['IMAGE'];

interface KeysResponse {
  vaultConfigured: boolean;
  /** La matrice CÂBLÉE du serveur : capacité → fournisseurs qui savent la servir. */
  capabilities: Record<string, string[]>;
  providerCapabilities: Record<string, string[]>;
  preferred: Record<string, string>;
  keys: { provider: string; capabilities: string[] }[];
}

/** Les fournisseurs RÉELLEMENT disponibles pour cette boutique, par surface, + son choix permanent. */
export interface AiProviderChoices {
  /** Fournisseurs dont une clé est posée et qui savent écrire. */
  text: string[];
  /** Idem pour les images. */
  image: string[];
  /**
   * TOUS ceux qui SAVENT faire ce travail, clé posée ou pas (fondateur, 27/08 : « ici même les
   * images je dois pouvoir choisir, higgsfield, nano banana »).
   *
   * Le menu ne montrait que les clés posées : avec une seule, il n'y avait pas de menu du tout, et
   * rien à l'écran ne disait qu'il existait deux autres dessinateurs à une clé près. On les montre
   * donc, éteints, avec ce qu'il faut faire pour les allumer. Ce qui ne SAIT PAS dessiner (Claude)
   * n'y figure pas : ce serait un choix qui ne commande rien.
   */
  wired: { text: string[]; image: string[] };
  /** Le choix permanent des Réglages : `COPY` (rédaction) et `IMAGE` (dessin). */
  preferred: { text: string | null; image: string | null };
}

const EMPTY: AiProviderChoices = {
  text: [],
  image: [],
  wired: { text: [], image: [] },
  preferred: { text: null, image: null },
};

/**
 * UNE SEULE LECTURE PAR SESSION. La pastille s'ouvre et se referme des dizaines de fois pendant une
 * séance de retouche ; refaire l'appel à chaque ouverture ferait clignoter le menu pour une réponse
 * qui ne change qu'au moment où la vendeuse pose une clé — ce qui se passe sur un AUTRE écran.
 */
let cached: Promise<AiProviderChoices> | null = null;

export const aiKeysApi = {
  /** Le menu du moment. Toute panne rend un menu VIDE : la retouche part alors sur la chaîne rangée. */
  async choices(): Promise<AiProviderChoices> {
    if (!cached) {
      cached = apiRequest<KeysResponse>(KEYS_PATH)
        .then((res) => {
          const able = (capability: string) =>
            (res.keys ?? [])
              .filter((k) => (k.capabilities ?? []).includes(capability))
              .map((k) => k.provider);
          return {
            text: able('TEXT'),
            image: able('IMAGE'),
            wired: {
              text: res.capabilities?.TEXT ?? [],
              image: res.capabilities?.IMAGE ?? [],
            },
            preferred: {
              text: res.preferred?.COPY ?? null,
              image: res.preferred?.IMAGE ?? null,
            },
          };
        })
        .catch(() => EMPTY);
    }
    return cached;
  },

  /**
   * « CELLE-LÀ, DÉSORMAIS, C'EST ELLE QUI LE FAIT. »
   *
   * Le choix fait devant la pastille devient le choix par défaut de la prochaine fois — sinon il
   * faudrait le refaire à chaque retouche, et une préférence qu'on doit répéter n'en est pas une.
   * Écrit côté serveur (donc retrouvé sur un autre poste), et reflété tout de suite dans le cache :
   * le menu ne doit pas revenir en arrière sous les yeux de la vendeuse pendant l'aller-retour.
   *
   * Sans effet de bord visible en cas d'échec : la retouche EN COURS part avec le fournisseur choisi
   * de toute façon (il voyage dans l'appel), seule la mémoire du choix serait perdue.
   */
  async prefer(surface: 'text' | 'image', provider: string): Promise<void> {
    const current = await this.choices();
    cached = Promise.resolve({
      ...current,
      preferred: { ...current.preferred, [surface]: provider },
    });
    const jobs = surface === 'text' ? TEXT_JOBS : IMAGE_JOBS;
    await Promise.allSettled(
      jobs.map((job) =>
        apiRequest<void>(PREFERRED_PATH, { method: 'PUT', body: { job, provider } }),
      ),
    );
  },

  /** Après une pose de clé ailleurs (ou dans un test) — la prochaine ouverture relit. */
  reset(): void {
    cached = null;
  },
};
